import type { MigrationState, ReviewItem } from "../../api/types";

/**
 * The states whose sign-off gate the generic Review panel answers for (`api/routers/actions.py::_BLOCKING_MESSAGES`,
 * mirrored by `runner.signoff`): scope, mapping and hypercare. NOT `awaiting_conversion_signoff`: the Dry-run
 * screen owns that stage, and a VAT_LOCK block there is acknowledged only in the sign-off dialog
 * (`workflow/vat_lock_signoff.py`), COA items through the mapping -- never from this panel.
 */
const SIGNOFF_GATE_STATES: ReadonlySet<MigrationState> = new Set<MigrationState>([
  "awaiting_scope_signoff",
  "awaiting_mapping_review",
  "hypercare",
]);

/**
 * True for an open BLOCK item that refuses the CURRENT state's sign-off. The Review screen is read-only outside
 * the review stage, but such an item must stay answerable from wherever the operator is, or the sign-off is a
 * dead end (a SOURCE item filed at scope time left the scope sign-off unreachable from the web). Mapping rows
 * (the tax, contact and dimension panels) do not use this: they stay read-only.
 */
export function gatesCurrentSignoff(item: ReviewItem, state: MigrationState): boolean {
  return (
    SIGNOFF_GATE_STATES.has(state) &&
    item.severity === "BLOCK" &&
    (item.resolved_at === null || item.resolved_at === undefined)
  );
}

/** `mapping/review.py::CANCEL_MIGRATION_OPTIONS` -- the answers that cancel the migration. */
const CANCEL_OPTIONS: ReadonlySet<string> = new Set<string>([
  "cancel",
  "cancel this migration and start a new one with an HMRC connection attached before extraction",
]);

/**
 * Whether answering a review item may move the migration's state, so the poll should watch for the transition.
 * Only the conversion sign-off (the answer lets it proceed) or a cancel option (the migration is cancelled) can;
 * an ordinary scope/mapping answer leaves the state where it is and must not trigger the fast poll.
 */
export function resolveMayMoveState(state: MigrationState, option: string): boolean {
  return state === "awaiting_conversion_signoff" || CANCEL_OPTIONS.has(option);
}
